"use strict";

import { baseUrl, growSdkEnvironment, handleRedirect } from "@/modules/configuration.js";

const SESSION_EXPIRED_HE = "פג תוקף התשלום, נא לנסות שוב";
const AUTH_CODE_RETRY_HE =
  "פג תוקף תהליך התשלום. לחצו שוב על כפתור התשלום לקבלת קוד חדש.";

const GROW_SDK_API_VERSION = 1;
const GROW_SDK_SCRIPT_URL = "https://cdn.meshulam.co.il/sdk/gs.min.js";
const GROW_MESHULAM_HOST = "cdn.meshulam.co.il";
const GROW_WALLET_CONTAINER_ID = "grow-wallet-container";
export const GROW_WALLET_CHANGE_EVENT = "yummi-grow-wallet-change";

function dispatchGrowWalletChange(open) {
  document.dispatchEvent(
    new CustomEvent(GROW_WALLET_CHANGE_EVENT, { detail: { open: Boolean(open) } })
  );
}

function focusGrowWallet() {
  const container = document.getElementById(GROW_WALLET_CONTAINER_ID);
  if (container) {
    const iframe = container.querySelector("iframe");
    if (iframe) {
      iframe.focus();
      return;
    }
    const focusable = container.querySelector(
      'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === "function") {
      focusable.focus();
      return;
    }
  }

  const meshulamFrame = document.querySelector('iframe[src*="meshulam"]');
  if (meshulamFrame && typeof meshulamFrame.focus === "function") {
    meshulamFrame.focus();
  }
}

export function isGrowWalletFocusContext(element) {
  const target = element || (typeof document !== "undefined" ? document.activeElement : null);
  if (!target) {
    return false;
  }
  const container = document.getElementById(GROW_WALLET_CONTAINER_ID);
  if (container?.contains(target)) {
    return true;
  }
  if (target.tagName === "IFRAME" && target.src?.includes("meshulam")) {
    return true;
  }
  return Boolean(target.closest?.('iframe[src*="meshulam"]'));
}

/** Active popup session — handlers are read at event time (init runs once per docs). */
let activeSession = null;

let preparePromise = null;

/** Grow authCode is single-use — block second renderPaymentOptions with the same code. */
let consumedAuthCode = null;

export function setGrowWalletSession(session) {
  activeSession = session;
}

export function clearGrowWalletSession() {
  activeSession = null;
}

export function normalizeGrowEnvironment(environment) {
  if (environment == null || environment === "") {
    return null;
  }
  const key = String(environment).trim().toUpperCase();
  if (key === "DEV" || key === "DEVELOPMENT" || key === "SANDBOX") {
    return "DEV";
  }
  if (key === "PRODUCTION" || key === "PROD" || key === "LIVE") {
    return "PRODUCTION";
  }
  return null;
}

/** Hardcoded per deliverable in configuration.js (like baseUrl). */
export function getDefaultGrowEnvironment() {
  return normalizeGrowEnvironment(growSdkEnvironment);
}

export function isAuthCodeConsumed(authCode) {
  const code = normalizeAuthCode(authCode);
  return Boolean(code && consumedAuthCode && code === consumedAuthCode);
}

/** True when renderPaymentOptions may run for this auth_code. */
export function canOpenGrowWallet(authCode) {
  return Boolean(normalizeAuthCode(authCode)) && !isAuthCodeConsumed(authCode);
}

export function normalizeAuthCode(authCode) {
  if (authCode == null) {
    return "";
  }
  if (typeof authCode === "object") {
    return normalizeAuthCode(authCode.authCode || authCode.auth_code || "");
  }
  return String(authCode).trim();
}

function growErrorMessage(response) {
  if (!response) {
    return "";
  }
  if (typeof response === "string") {
    return response;
  }
  return response.message || response.err?.message || "";
}

function hardResetGrowSdk() {
  window.__growSdkBootstrapped = false;
  window.__growSdkEnv = null;
  preparePromise = null;
  window.__growSdkPromise = null;
  delete window.growRuntime;
  delete window.growPayment;
  document
    .querySelectorAll('script[src*="cdn.meshulam.co.il"]')
    .forEach((el) => el.remove());
}

function buildGrowInitConfig(environment) {
  const env = normalizeGrowEnvironment(environment);
  if (!env) {
    throw new Error("Grow SDK environment must be DEV or PRODUCTION");
  }
  return {
    environment: env,
    version: GROW_SDK_API_VERSION,
    events: buildEvents(),
  };
}

/**
 * Grow docs: configureGrowSdk — growPayment.init on gs.min.js load.
 * https://grow-il.readme.io — environment from configuration.js per build.
 */
export async function configureGrowSdk(environment) {
  const env = normalizeGrowEnvironment(environment) || getDefaultGrowEnvironment();
  if (!env) {
    throw new Error(
      "growSdkEnvironment is not set in configuration.js (use DEV or PRODUCTION)"
    );
  }
  return bootstrapGrowSdk(env);
}

/**
 * Grow docs: load gs.min.js; onload calls configureGrowSdk (init).
 */
export function loadGrowSdk() {
  if (window.__growSdkPromise) {
    return window.__growSdkPromise;
  }
  window.__growSdkPromise = new Promise((resolve, reject) => {
    if (window.growPayment && window.__growSdkBootstrapped) {
      resolve(window.growPayment);
      return;
    }
    if (window.growPayment) {
      configureGrowSdk()
        .then(() => resolve(window.growPayment))
        .catch(reject);
      return;
    }
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = GROW_SDK_SCRIPT_URL;
    s.onload = () => {
      if (!window.growPayment) {
        reject(new Error("growPayment not available after script load"));
        return;
      }
      configureGrowSdk()
        .then(() => resolve(window.growPayment))
        .catch(reject);
    };
    s.onerror = () => reject(new Error("Failed to load Grow SDK script"));
    const x = document.getElementsByTagName("script")[0];
    x.parentNode.insertBefore(s, x);
  });
  return window.__growSdkPromise;
}

function waitForGrowRuntime(maxMs = 45000) {
  return new Promise((resolve, reject) => {
    if (window.growRuntime) {
      resolve();
      return;
    }
    const started = Date.now();
    const tick = () => {
      if (window.growRuntime) {
        resolve();
        return;
      }
      if (Date.now() - started > maxMs) {
        reject(new Error("Grow runtime did not initialize in time"));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function waitForGrowWalletServicesReady(maxMs = 45000) {
  return new Promise((resolve) => {
    const started = Date.now();
    let lastActivity = Date.now();

    let observer = null;
    try {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name && entry.name.includes(GROW_MESHULAM_HOST)) {
            lastActivity = Date.now();
          }
        }
      });
      observer.observe({ entryTypes: ["resource"] });
    } catch {
      observer = null;
    }

    const meshulamResources = () =>
      performance
        .getEntriesByType("resource")
        .filter((e) => e.name && e.name.includes(GROW_MESHULAM_HOST));

    const tick = () => {
      const resources = meshulamResources();
      const hasAssets = resources.some(
        (e) => e.name.includes("/js/") || e.name.includes("/css/")
      );
      const anyInFlight = resources.some(
        (e) => e.responseEnd === 0 && e.duration === 0 && e.startTime >= started - 2000
      );
      const idleMs = Date.now() - lastActivity;
      const ready = window.growRuntime && hasAssets && !anyInFlight && idleMs >= 400;

      if (ready) {
        if (observer) observer.disconnect();
        resolve();
        return;
      }

      if (Date.now() - started > maxMs) {
        if (observer) observer.disconnect();
        resolve();
        return;
      }
      setTimeout(tick, 100);
    };

    tick();
  });
}

function initGrowPaymentAndWait(config) {
  return new Promise((resolve, reject) => {
    if (window.growRuntime && window.__growSdkEnv === config.environment) {
      Promise.resolve(window.growPayment.init(config))
        .then(() => waitForGrowWalletServicesReady())
        .then(resolve)
        .catch(reject);
      return;
    }
    if (window.growRuntime && window.__growSdkEnv !== config.environment) {
      hardResetGrowSdk();
    }

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Grow SDK init timed out"));
    }, 45000);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node?.tagName === "SCRIPT" && node.src?.includes("/js/mp.min.js")) {
            node.addEventListener("load", onMpLoaded, { once: true });
            node.addEventListener("error", onMpError, { once: true });
          }
        }
      }
    });

    function cleanup() {
      clearTimeout(timeout);
      observer.disconnect();
    }

    function onMpError() {
      cleanup();
      reject(new Error("Failed to load Grow payment runtime (mp.min.js)"));
    }

    async function onMpLoaded() {
      try {
        await waitForGrowRuntime();
        await waitForGrowWalletServicesReady();
        cleanup();
        resolve();
      } catch (err) {
        cleanup();
        reject(err);
      }
    }

    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.growPayment.init(config).catch((err) => {
      cleanup();
      reject(err);
    });
  });
}

/** Ensures script load + configureGrowSdk (doc flow) completed. */
export function prepareGrowSdk(environment) {
  const env = normalizeGrowEnvironment(environment) || getDefaultGrowEnvironment();
  if (preparePromise && window.__growSdkEnv === env) {
    return preparePromise;
  }
  preparePromise = (async () => {
    await loadGrowSdk();
    if (!window.__growSdkBootstrapped || window.__growSdkEnv !== env) {
      await configureGrowSdk(env);
    } else if (typeof window.growPayment.updateEvents === "function") {
      window.growPayment.updateEvents(buildEvents());
    }
    await waitForGrowWalletServicesReady(5000);
  })().catch((err) => {
    preparePromise = null;
    throw err;
  });
  return preparePromise;
}

export async function bootstrapGrowSdk(environment) {
  const config = buildGrowInitConfig(environment);
  const env = config.environment;

  if (window.__growSdkBootstrapped && env !== window.__growSdkEnv) {
    hardResetGrowSdk();
  }

  if (!window.growPayment) {
    await loadGrowSdk();
  }

  if (!window.__growSdkBootstrapped) {
    await initGrowPaymentAndWait(config);
    window.__growSdkBootstrapped = true;
    window.__growSdkEnv = env;
    return;
  }

  if (typeof window.growPayment.updateEvents === "function") {
    window.growPayment.updateEvents(buildEvents());
  }
  await waitForGrowWalletServicesReady(5000);
}

function buildEvents() {
  return {
    onSuccess(response) {
      activeSession?.handlers?.onSuccess?.(response);
    },
    onFailure(response) {
      activeSession?.handlers?.onFailure?.(response);
    },
    onError(response) {
      activeSession?.handlers?.onError?.(response);
    },
    onTimeout(response) {
      activeSession?.handlers?.onTimeout?.(response);
    },
    onWalletChange(state) {
      activeSession?.handlers?.onWalletChange?.(state);
    },
    onPaymentStart(response) {
      activeSession?.handlers?.onPaymentStart?.(response);
    },
    onPaymentCancel(response) {
      activeSession?.handlers?.onPaymentCancel?.(response);
    },
  };
}

function setPopupErrorMessage(message) {
  const el = document.getElementById("popup-error-message");
  if (el) {
    el.innerHTML = message || "";
  }
}

export async function confirmMeshulamPayment(body) {
  const url = baseUrl("/orders/meshulam_sdk_confirm");
  const formData = new FormData();
  if (body.confirmation_number != null) {
    formData.append("confirmation_number", body.confirmation_number);
  }
  if (body.payment_method != null) {
    formData.append("payment_method", body.payment_method);
  }

  const response = await fetch(url, {
    method: "POST",
    redirect: "manual",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });

  if (response.status === 404) {
    return { error: true, message: "Not found" };
  }
  if (response.status !== 200) {
    return { error: true, message: "Request failed" };
  }

  const data = await response.json();
  if (handleRedirect(data)) {
    return { redirected: true };
  }
  return data;
}

export function invalidateGrowWalletAttempt() {
  teardownGrowWallet();
}

export function makeSdkHandlers({ $this, onClose }) {
  const clearTimer = () => {
    if (typeof $this.clearGrowSafetyTimer === "function") {
      $this.clearGrowSafetyTimer();
    }
  };

  const closePopup = () => {
    clearTimer();
    invalidateGrowWalletAttempt();
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const endAttemptWithError = (message) => {
    invalidateGrowWalletAttempt();
    if ($this.popupLoadingIndicator !== undefined) {
      $this.popupLoadingIndicator = false;
    }
    setPopupErrorMessage(message);
  };

  return {
    onSuccess(response) {
      clearTimer();
      const data = response?.data || {};
      confirmMeshulamPayment({
        confirmation_number: data.confirmation_number,
        payment_method: data.payment_method,
      }).then((result) => {
        if (result?.redirected) {
          closePopup();
          return;
        }
        if (result?.error || result?.message) {
          setPopupErrorMessage(result.message || result.error);
          closePopup();
        }
      }).catch((err) => {
        console.error("meshulam_sdk_confirm failed", err);
        setPopupErrorMessage("אירעה שגיאה באישור התשלום");
        closePopup();
      });
    },

    onFailure(response) {
      clearTimer();
      endAttemptWithError(growErrorMessage(response) || "תשלום נכשל");
    },

    onError(response) {
      clearTimer();
      console.error("Grow payment error", response);
      endAttemptWithError(growErrorMessage(response) || "אירעה שגיאה בתשלום");
    },

    onTimeout() {
      clearTimer();
      setPopupErrorMessage(SESSION_EXPIRED_HE);
      closePopup();
    },

    onWalletChange(state) {
      if (state === "open") {
        $this.popupLoadingIndicator = false;
        dispatchGrowWalletChange(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => focusGrowWallet());
        });
      } else if (state === "close") {
        $this.popupLoadingIndicator = true;
        dispatchGrowWalletChange(false);
      }
    },

    onPaymentStart(response) {
      console.debug("Grow payment start", response);
    },

    onPaymentCancel() {
      clearTimer();
      closePopup();
    },
  };
}

export { AUTH_CODE_RETRY_HE };

/**
 * After createPaymentProcess (auth_code): renderPaymentOptions only (Grow docs).
 */
export async function renderGrowWallet(authCode, environment) {
  const code = normalizeAuthCode(authCode);
  if (!code) {
    throw new Error("auth_code is required");
  }
  if (isAuthCodeConsumed(code)) {
    const err = new Error("Grow auth_code already used");
    err.code = "GROW_AUTH_CODE_CONSUMED";
    throw err;
  }
  const env =
    normalizeGrowEnvironment(environment) ||
    normalizeGrowEnvironment(activeSession?.env) ||
    getDefaultGrowEnvironment();
  if (!env) {
    throw new Error(
      "Grow SDK environment missing: set form.env or growSdkEnvironment in configuration.js"
    );
  }
  await waitForWalletContainer();
  await prepareGrowSdk(env);
  if (!window.growPayment) {
    throw new Error("growPayment is not loaded");
  }
  consumedAuthCode = code;
  window.growPayment.renderPaymentOptions(code);
}

function waitForWalletContainer(maxMs = 5000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const el = document.getElementById(GROW_WALLET_CONTAINER_ID);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - started > maxMs) {
        reject(new Error("Grow wallet container not found in DOM"));
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

export function teardownGrowWallet() {
  clearGrowWalletSession();
  dispatchGrowWalletChange(false);
  const container = document.getElementById(GROW_WALLET_CONTAINER_ID);
  if (container) {
    container.innerHTML = "";
  }
}

/** After successful payment or new checkout session — optional explicit reset. */
export function resetConsumedAuthCode() {
  consumedAuthCode = null;
}
